import { useEffect, useMemo, useRef, useState } from 'react'
import { Languages, Pencil, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react'
import { Badge, Banner, Button, ConfirmDialog, ErrorState, Field, Textarea, fmtDateTime, useToast } from '../kit'
import { useChartSave } from '../lib/chartSave.jsx'
import { clinicalHash, isClinicalEmpty, saveSummaryEdit, streamTranslate, validSummary } from '../lib/aiTranslate.js'

/**
 * AI-Translation — the doctor's note in plain Thai for the non-medical front-counter
 * staff who book, bill and answer the patient from it. Lives inside the Clinical card:
 * the button in its header (<AiTranslateButton>), the result at its foot (<AiTranslation>),
 * both driven by one useAiTranslate() state. Spec + owner decisions:
 * docs/ai-translate-spec.md. Stored on `chart.ai_summary`, written only by
 * /api/charts/:id/translate.
 *
 *   - Any logged-in user can translate and edit (owner decision 2026-10-25).
 *   - Translating is offered only when there is no translation yet or the clinical text
 *     changed since; the server refuses an unchanged chart before spending tokens.
 *   - Re-translating over a manual edit asks first, then replaces it entirely.
 */
export function useAiTranslate({ chart, aiSummary, patientId, treatmentId }) {
  const toast = useToast()
  const chartId = chart?.id
  // The latest write we made ourselves wins until the chart (or realtime) catches up.
  const [local, setLocal] = useState(null)
  const saved = newer(local?.chartId === chartId ? local.summary : null, validSummary(aiSummary) ? aiSummary : null)

  const [hash, setHash] = useState(null)
  useEffect(() => {
    let alive = true
    setHash(null)
    if (chart) clinicalHash(chart).then((h) => { if (alive) setHash(h) })
    return () => { alive = false }
  }, [chart])
  const empty = !chart || isClinicalEmpty(chart)
  const stale = !!(saved && hash && saved.source_hash !== hash)
  const canTranslate = !!chartId && !empty && hash !== null && (!saved || stale)

  const [streaming, setStreaming] = useState(null) // translation so far, while it is written
  const [failed, setFailed] = useState(null)
  const [askOverwrite, setAskOverwrite] = useState(false)
  const abortRef = useRef(null)
  useEffect(() => () => abortRef.current?.abort(), [])

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ patient_summary: '', staff_notes: '' })
  const [saving, setSaving] = useState(false)
  const dirty = editing && !!saved && (
    draft.patient_summary !== (saved.patient_summary || '') || draft.staff_notes !== (saved.staff_notes || ''))

  async function translate(overwriteEdits = false) {
    setAskOverwrite(false)
    setFailed(null)
    setStreaming('')
    abortRef.current = new AbortController()
    try {
      const s = await streamTranslate({
        chartId, patientId, treatmentId, overwriteEdits,
        signal: abortRef.current.signal,
        onText: (t) => setStreaming(t),
      })
      setLocal({ chartId, summary: s })
      setEditing(false)
      toast.success('แปลเสร็จแล้ว')
    } catch (e) {
      if (e?.name === 'AbortError') return
      // Another screen translated or edited first — the realtime refresh brings it in.
      if (e?.code === 'unchanged') toast.info('ข้อมูล chart ไม่เปลี่ยน', { detail: 'แก้คำแปลได้โดยตรง' })
      else if (e?.code === 'edited') setAskOverwrite(true)
      else setFailed(e?.message || 'แปลไม่สำเร็จ')
    } finally {
      setStreaming(null)
    }
  }
  function start() {
    if (saved?.edited_at) setAskOverwrite(true)
    else translate(false)
  }

  function startEdit() {
    setDraft({ patient_summary: saved?.patient_summary || '', staff_notes: saved?.staff_notes || '' })
    setEditing(true)
  }
  function cancelEdit() { setEditing(false) }
  async function save() {
    if (!dirty) { setEditing(false); return true }
    setSaving(true)
    try {
      const s = await saveSummaryEdit({
        chartId, patientId, treatmentId,
        patientSummary: draft.patient_summary, staffNotes: draft.staff_notes,
      })
      setLocal({ chartId, summary: s })
      setEditing(false)
      return true
    } catch (e) {
      toast.error('บันทึกไม่สำเร็จ', { detail: e?.message })
      return false
    } finally {
      setSaving(false)
    }
  }
  useChartSave('ai-translate', dirty, save, cancelEdit)
  async function saveAndConfirm() {
    if (await save()) toast.success('บันทึกแล้ว')
  }

  return {
    saved, stale, canTranslate, busy: streaming !== null, streaming, failed,
    askOverwrite, setAskOverwrite, translate, start, stop: () => abortRef.current?.abort(),
    editing, draft, setDraft, saving, startEdit, cancelEdit, saveAndConfirm,
  }
}

// Clinical header button. Shown only while translating is possible, so an up-to-date
// translation never offers a paid no-op.
export function AiTranslateButton({ ai }) {
  if (ai.busy) return <Button variant="secondary" size="sm" onClick={ai.stop}>หยุดแปล</Button>
  if (!ai.canTranslate || ai.editing) return null
  return (
    <Button variant="secondary" size="sm" icon={ai.saved ? RefreshCw : Languages} onClick={ai.start}>
      {ai.saved ? 'แปลใหม่' : 'แปลด้วย AI'}
    </Button>
  )
}

// The result, at the foot of the Clinical card.
export function AiTranslation({ ai }) {
  const [showNotes, setShowNotes] = useState(true)
  const { saved } = ai
  const dialog = ai.askOverwrite && (
    <ConfirmDialog
      tone="danger"
      title="แปลใหม่แทนข้อความที่แก้?"
      message={`คำแปลนี้ถูกแก้ไขโดย ${saved?.edited_by_name || 'เจ้าหน้าที่'} — แปลใหม่จะแทนที่ข้อความที่แก้ทั้งหมด`}
      confirmLabel="แปลใหม่"
      onCancel={() => ai.setAskOverwrite(false)}
      onConfirm={() => ai.translate(true)}
    />
  )
  if (!saved && !ai.busy && !ai.failed) return dialog || null

  return (
    <div className="rounded-xl ring-1 ring-brand-100 bg-brand-50/30">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <Languages size={14} className="text-brand-600" />
        <div className="flex-1 text-[12.5px] uppercase tracking-[0.14em] font-semibold text-brand-700">AI-Translation</div>
        {saved && !ai.busy && !ai.editing && (
          <Button variant="ghost" size="sm" icon={Pencil} onClick={ai.startEdit}>แก้ไขคำแปล</Button>
        )}
        {ai.editing && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={ai.cancelEdit} disabled={ai.saving}>ยกเลิก</Button>
            <Button size="sm" onClick={ai.saveAndConfirm} loading={ai.saving}>บันทึก</Button>
          </div>
        )}
      </div>
      <div className="px-4 pb-4 space-y-3">
        {ai.failed && !ai.busy && (
          <ErrorState title="แปลไม่สำเร็จ ลองใหม่อีกครั้ง" message={ai.failed} onRetry={ai.canTranslate ? ai.start : undefined} />
        )}
        {ai.stale && !ai.busy && !ai.editing && (
          <Banner tone="warn">chart มีการแก้ไขหลังแปล · กด แปลใหม่ ด้านบนเพื่ออัปเดต</Banner>
        )}
        {ai.busy && (
          <div>
            <div className="text-[12.5px] text-slate-400 mb-2">กำลังแปล… (ประมาณ 20 วินาที)</div>
            {ai.streaming ? <SummaryText text={ai.streaming} /> : <div className="h-16 rounded-xl bg-white/70 animate-pulse" />}
          </div>
        )}
        {!ai.busy && !ai.editing && saved && (
          <>
            <SummaryText text={saved.patient_summary} />
            <StaffNotes notes={saved.staff_notes} open={showNotes} onToggle={() => setShowNotes((v) => !v)} />
            <Stamp summary={saved} />
          </>
        )}
        {ai.editing && (
          <div className="space-y-3">
            <Field label="คำแปล" hint='หัวข้อขึ้นต้นด้วย "## " · รายการขึ้นต้นด้วย "- " · ถ้าแปลใหม่ภายหลัง ข้อความที่แก้จะถูกแทนที่ทั้งหมด'>
              <Textarea rows={14} value={ai.draft.patient_summary}
                onChange={(e) => ai.setDraft({ ...ai.draft, patient_summary: e.target.value })} />
            </Field>
            <Field label="ข้อควรถามทันตแพทย์">
              <Textarea rows={3} value={ai.draft.staff_notes}
                onChange={(e) => ai.setDraft({ ...ai.draft, staff_notes: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
      {dialog}
    </div>
  )
}

function newer(a, b) {
  if (!a) return b
  if (!b) return a
  const t = (s) => Math.max(Number(s.created_at) || 0, Number(s.edited_at) || 0)
  return t(a) >= t(b) ? a : b
}

function Stamp({ summary }) {
  return (
    <div className="text-[12.5px] text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
      <span>แปลโดย AI · {fmtDateTime(summary.created_at)}{summary.created_by_name ? ` · ${summary.created_by_name}` : ''}</span>
      {summary.edited_at && (
        <span className="text-amber-600">แก้ไข {fmtDateTime(summary.edited_at)}{summary.edited_by_name ? ` · ${summary.edited_by_name}` : ''}</span>
      )}
    </div>
  )
}

function StaffNotes({ notes, open, onToggle }) {
  const text = String(notes || '').trim()
  if (!text) return null
  const count = text.split('\n').filter((l) => l.trim()).length
  return (
    <div className="rounded-xl bg-amber-50/70 ring-1 ring-amber-100">
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] font-medium text-amber-800">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        ข้อควรถามทันตแพทย์ <Badge tone="amber">{count}</Badge>
      </button>
      {open && <div className="px-3 pb-3"><SummaryText text={text} size="sm" /></div>}
    </div>
  )
}

/**
 * The tiny markdown the prompt asks for: "## " headings, "- " / "1. " items, **bold**.
 * Built as React nodes, never innerHTML — the text is model output.
 */
function SummaryText({ text, size = 'md' }) {
  const blocks = useMemo(() => parseBlocks(text), [text])
  const body = size === 'sm' ? 'text-[13.5px]' : 'text-[15px] leading-relaxed'
  return (
    <div className={`${body} text-slate-800`}>
      {blocks.map((b, i) => b.type === 'h'
        ? <div key={i} className="text-[15px] mt-4 first:mt-0 font-semibold text-brand-800 mb-1">{inline(b.text)}</div>
        : b.type === 'li'
          ? <div key={i} className="flex gap-2 pl-1"><span className="text-slate-400 flex-none">{b.mark}</span><span>{inline(b.text)}</span></div>
          : <p key={i} className="mb-1">{inline(b.text)}</p>)}
    </div>
  )
}

function parseBlocks(text) {
  return String(text || '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    let m
    if ((m = /^#{1,6}\s+(.*)$/.exec(l))) return { type: 'h', text: m[1] }
    if ((m = /^[-*•]\s+(.*)$/.exec(l))) return { type: 'li', mark: '•', text: m[1] }
    if ((m = /^(\d+)[.)]\s+(.*)$/.exec(l))) return { type: 'li', mark: `${m[1]}.`, text: m[2] }
    return { type: 'p', text: l }
  })
}

function inline(s) {
  return String(s).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> : part)
}
